import { faRectangleVerticalHistory } from "@fortawesome/pro-regular-svg-icons/faRectangleVerticalHistory";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CardContent, IconButton, useTheme } from '@mui/material';
import { MprcCard, MprcContainer, MprcInfiniteLoading, MprcRequestErrorReport } from 'components';
import { FeedItemDto, FeedType } from 'generated/CoreApi_v1';
import { useWindowSize } from "hooks/useWindowSize";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { useGobackOrKnowledge } from "views/common/hooks/useGobackOrHome";
import { CommentBox } from 'views/knowledge/components/comment/CommentBox';
import { PublicationContentBox } from "views/knowledge/publication/pages/components/PublicationContentBox";
import { RightPanelContentPage } from "../common/RightPanelContentPage";
import { PublicationCardHeader } from '../components/PublicationCardHeader';
import { PublicationHeader } from "../components/PublicationHeader";

////////////////////////////////////////////////////////////////////////////////////////////////

export const PostThreadViewer: React.FC = () => {

    const { id: publicationId } = useParams();

    const location = useLocation();
    const gobackOrHome = useGobackOrKnowledge();

    const api = useCoreApi();
    const theme = useTheme();
    const { width } = useWindowSize({ wait: 50 });

    const [loading, setLoading] = useState<boolean>(true);
    const [publication, setPublication] = useState<FeedItemDto>();
    const [viewError, setViewError] = useState<any>();
    const [openRightPanel, setOpenRightPanel] = useState<boolean>(false);

    ///
    const load = async (publicationId: string) => {
        let markRead = true;

        // Se a solicitação foi realizada a partir do Feed de Histórico,
        // não deve marcar a Publicação como 'Lida', para não alterar o histórico de visualização.
        if (location.state?.hasOwnProperty("previousPathname")) {
            const previousPathname = location.state.previousPathname as string;
            markRead = !previousPathname.startsWith(KnowledgePaths.FeedHistory);
        }

        setLoading(true);
        try {
            var result = await api.v1.knowledgeFeed.getFeedItem({ publicationId, markRead });
            setPublication(result.data);
        } catch (error: any) {
            setViewError(error);
        }
        finally {

            setLoading(false);
        }
    };

    ///
    const onDataItemChanged = (data: Partial<FeedItemDto>) => {
        setPublication(prevState => {
            return { ...prevState!, ...data };
        });
    };

    useEffect(() => {
        if (publicationId != null) {
            load(publicationId);
        }
    }, []);

    useEffect(() => {
        if (openRightPanel && width > theme.breakpoints.values["md"])
            setOpenRightPanel(false);
    }, [width]);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderPublication = useCallback(() => (
        <PublicationCardHeader
            feedType={FeedType.ThreadItemContentPage}
            publication={publication!}
            onNeedResetRowHeight={() => { }}
            onReload={() => load(publicationId!)}
            onFeedItemChanged={(data) => {
                if (data.deletedOn) gobackOrHome();
                else onDataItemChanged(data);
            }}
        />
    ), [publication]);

    const RenderComment = useCallback(() => {
        return (
            <>
                {
                    publication &&
                    <MprcCard>
                        <CardContent>
                            <CommentBox publicationId={publication.publicationId} publicationTitle={publication.title} readOnly={publication.readOnly} />
                        </CardContent>
                    </MprcCard>
                }
            </>
        );
    }, [publication]);

    return (
        <div>
            {
                viewError != undefined
                    ? <MprcRequestErrorReport error={viewError} />
                    : loading
                        ? <MprcInfiniteLoading visible />
                        : publication != undefined &&
                        <div id="postThreadViewerContainer">
                            <MprcContainer position="sticky" margin="none">
                                <PublicationHeader
                                    onClose={gobackOrHome}
                                    actions={
                                        width < theme.breakpoints.values["md"]
                                            ? [<IconButton onClick={() => setOpenRightPanel(state => !state)}><FontAwesomeIcon icon={faRectangleVerticalHistory} /></IconButton>]
                                            : []
                                    }
                                />
                            </MprcContainer>

                            <PublicationContentBox
                                openRightPanel={openRightPanel}
                                onCloseRighPanel={() => setOpenRightPanel(false)}
                                content={
                                    <MprcContainer>
                                        {RenderPublication()}
                                        {RenderComment()}
                                    </MprcContainer>
                                }
                                rightPanel={publication != undefined ? RightPanelContentPage(publication) : undefined}
                                maxWidth="lg"
                            />
                        </div>
            }
        </div>
    );
};
